import React, { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  FileWarning,
  Plus,
  X,
  Loader2,
  AlertCircle,
  ToggleLeft,
  ToggleRight,
  Calendar,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { DatePickerField } from '../../../components/DatePickerField';
import {
  justificativasAusenciaService,
  JustificativaAusencia,
} from '../../../services/justificativasAusencia';
import { usePermissions, PERMISSOES } from '../../../hooks/usePermissions';

// ─── Modal de Abrir JA ────────────────────────────────────────────────────────
interface AbrirJAModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AbrirJAModal({ open, onClose, onSuccess }: AbrirJAModalProps) {
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setDataInicio('');
      setDataFim('');
      setMotivo('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dataInicio || !dataFim) {
      enqueueSnackbar('Informe as datas de início e fim', { variant: 'error' });
      return;
    }
    if (new Date(dataInicio) >= new Date(dataFim)) {
      enqueueSnackbar('A data de início deve ser anterior à data de fim', { variant: 'error' });
      return;
    }
    if (!motivo.trim()) {
      enqueueSnackbar('Informe o motivo da ausência', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      await justificativasAusenciaService.abrir({ data_inicio: dataInicio, data_fim: dataFim, motivo });
      enqueueSnackbar('JA aberta com sucesso!', { variant: 'success' });
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao abrir JA', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-[9998]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl shadow-2xl w-full max-w-lg z-[9999] p-6">
          <div className="flex items-center justify-between mb-6">
            <Dialog.Title className="text-xl font-semibold text-foreground flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-yellow-500" />
              Abrir Justificativa de Ausência
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Início <span className="text-red-500">*</span>
                </label>
                <DatePickerField value={dataInicio} onChange={setDataInicio} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Data de Fim <span className="text-red-500">*</span>
                </label>
                <DatePickerField value={dataFim} onChange={setDataFim} required />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Descreva o motivo da ausência..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Abrindo...
                  </>
                ) : (
                  'Abrir JA'
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Card de JA ───────────────────────────────────────────────────────────────
interface JACardProps {
  ja: JustificativaAusencia;
  podeInativar: boolean;
  togglingId: string | null;
  onInativar: (id: string) => void;
}

function JACard({ ja, podeInativar, togglingId, onInativar }: JACardProps) {
  const [expanded, setExpanded] = useState(false);

  const now = new Date();
  const inicio = new Date(ja.data_inicio + 'T00:00:00');
  const fim = new Date(ja.data_fim + 'T23:59:59');
  const vigente = ja.ativo && now >= inicio && now <= fim;

  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');

  return (
    <Card className="overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors"
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          {ja.militar && (
            <img
              src={`https://www.habbo.com.br/habbo-imaging/avatarimage?&user=${encodeURIComponent(ja.militar.nick)}&action=std&direction=4&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
              alt={ja.militar.nick}
              className="w-10 h-10 object-cover flex-shrink-0"
            />
          )}
          <div>
            {ja.militar && (
              <p className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                {ja.militar.patente?.abreviacao && (
                  <span className="text-muted-foreground text-xs">{ja.militar.patente.abreviacao}</span>
                )}
                {ja.militar.nick}
                {ja.militar.sigla && (
                  <span className="text-xs text-muted-foreground">| {ja.militar.sigla.sigla}</span>
                )}
              </p>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Calendar className="h-3 w-3" />
              {formatDate(ja.data_inicio)} até {formatDate(ja.data_fim)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
          {vigente && (
            <Badge className="bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30 text-xs">
              Vigente
            </Badge>
          )}
          {ja.ativo && !vigente && (
            <Badge className="bg-blue-500/20 text-blue-600 dark:text-blue-400 border border-blue-500/30 text-xs">
              Ativa
            </Badge>
          )}
          {!ja.ativo && (
            <Badge variant="secondary" className="text-xs">Inativa</Badge>
          )}

          {podeInativar && ja.ativo && (
            <button
              onClick={() => onInativar(ja.id)}
              disabled={togglingId === ja.id}
              className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              title="Inativar JA"
            >
              {togglingId === ja.id ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ToggleRight className="h-6 w-6 text-yellow-500" />
              )}
            </button>
          )}
          {!ja.ativo && (
            <ToggleLeft className="h-6 w-6 text-muted-foreground opacity-40" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border px-4 py-3 bg-background">
          <p className="text-sm text-foreground">
            <span className="font-medium">Motivo: </span>
            {ja.motivo}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Aberta em {new Date(ja.criado_em).toLocaleDateString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </p>
        </div>
      )}
    </Card>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────
export function JustificativasAusencia() {
  const [jas, setJas] = useState<JustificativaAusencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const { hasAnyPermission } = usePermissions();
  const podeVerTodos = hasAnyPermission([PERMISSOES.GERENCIAR_JA_TODOS, PERMISSOES.GERENCIAR_JA_PRACAS]);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await justificativasAusenciaService.listar();
      setJas(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao carregar JAs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const handleInativar = async (id: string) => {
    setTogglingId(id);
    try {
      await justificativasAusenciaService.inativar(id);
      enqueueSnackbar('JA inativada com sucesso!', { variant: 'success' });
      await carregar();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao inativar JA', { variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  // Um militar sem permissão especial pode inativar apenas as próprias JAs
  // (a restrição de quem vê o quê já vem do backend; aqui qualquer JA listada pode ser inativada pelo dono)
  const podeInativarJA = (ja: JustificativaAusencia) => {
    // Admins com permissão especial podem inativar qualquer JA visível
    // Militares comuns podem inativar as próprias (só vêem as próprias mesmo)
    return true;
  };

  const jaAtiva = jas.find((j) => j.ativo);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileWarning className="h-6 w-6 text-yellow-500" />
            Justificativas de Ausência
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {podeVerTodos
              ? 'Visualize e gerencie as JAs dos militares'
              : 'Gerencie suas justificativas de ausência'}
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-yellow-600 hover:bg-yellow-700 text-white flex items-center gap-2"
          disabled={!!jaAtiva}
          title={jaAtiva ? 'Você já possui uma JA ativa' : undefined}
        >
          <Plus className="h-4 w-4" />
          Abrir JA
        </Button>
      </div>

      {jaAtiva && (
        <div className="flex items-center gap-2 px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md text-sm text-yellow-700 dark:text-yellow-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          Você já possui uma JA ativa. Inative-a antes de abrir uma nova.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : jas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-foreground font-medium">Nenhuma justificativa de ausência encontrada.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Clique em "Abrir JA" para registrar uma ausência.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {jas.map((ja) => (
            <JACard
              key={ja.id}
              ja={ja}
              podeInativar={podeInativarJA(ja)}
              togglingId={togglingId}
              onInativar={handleInativar}
            />
          ))}
        </div>
      )}

      <AbrirJAModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          carregar();
        }}
      />
    </div>
  );
}
